import React, { useEffect, useRef, useState } from 'react'
import { Copy, Check } from 'lucide-react'
import { Button } from 'components/ui'
import { ChatMessage } from 'components/ui/ChatMessage'
import { ChatMarkdown } from 'components/ui/ChatMarkdown'
import { ChatThinking } from 'components/ui/ChatActivity'
import { ConversationMessage } from 'components/GPTConversation/ConversationModal'
import { usePromptGenerator } from '../../PromptGeneratorContext'
import chatTheme from 'components/ui/ChatSurface/styles.module.css'
import styles from './styles.module.css'

const PromptMessage = React.memo(({ message }: { message: ConversationMessage }) => {
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState(false)

  useEffect(() => {
    setCopied(false)
    setCopyError(false)
  }, [message.message])

  return (
    <ChatMessage
      user={message.isUserMessage}
      aria-label={message.isUserMessage ? 'Your message' : 'Assistant message'}
      meta={
        message.message && (
          <Button
            variant="ghost"
            fontSize="11.5px"
            color="#8C8C84"
            h="24px"
            px="4px"
            startIcon={copied ? <Check size={12} /> : <Copy size={12} />}
            onClick={() =>
              navigator.clipboard
                .writeText(message.message)
                .then(() => setCopied(true))
                .catch(() => setCopyError(true))
            }
          >
            {copyError ? 'Copy unavailable' : copied ? 'Copied' : 'Copy'}
          </Button>
        )
      }
    >
      {message.isUserMessage ? message.message : <ChatMarkdown>{message.message}</ChatMarkdown>}
    </ChatMessage>
  )
})

export const PromptGeneratorConversation: React.FC = () => {
  const {
    conversation,
    isBotResponseInProgress,
    isWebSocketReady,
    hasWebSocketError,
    isSessionExpired,
    reconnect,
    reloadSession,
  } = usePromptGenerator()
  const end = useRef<HTMLDivElement>(null)
  const scroll = useRef<HTMLDivElement>(null)
  const following = useRef(true)

  // WebSocket stays busy until res_complete, even after response text starts arriving.
  // Waiting for the first text is a presentation state, separate from the send lock.
  const lastMessage = conversation[conversation.length - 1]
  const hasStartedAnswer = !!lastMessage && !lastMessage.isUserMessage && !!lastMessage.message.trim()
  const showThinking =
    (isBotResponseInProgress && !hasStartedAnswer) || (!isWebSocketReady && conversation.length === 0)

  useEffect(() => {
    if (following.current) end.current?.scrollIntoView({ block: 'nearest' })
  }, [conversation, isBotResponseInProgress])

  return (
    <div
      ref={scroll}
      className={[chatTheme.surface, styles.body].join(' ')}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
      onScroll={() => {
        const node = scroll.current
        if (node) following.current = node.scrollHeight - node.scrollTop - node.clientHeight < 80
      }}
    >
      {isSessionExpired ? (
        <div className={styles.state} role="alert">
          Session expired.
          <Button variant="secondary" onClick={reloadSession}>
            Reconnect
          </Button>
        </div>
      ) : hasWebSocketError ? (
        <div className={styles.state} role="alert">
          Unable to connect to the server.
          <Button variant="secondary" onClick={reconnect}>
            Reconnect
          </Button>
        </div>
      ) : (
        <div className={styles.messages}>
          {conversation.map((message, index) => (
            <PromptMessage key={index} message={message} />
          ))}
          {showThinking && <ChatThinking />}
          <div ref={end} />
        </div>
      )}
    </div>
  )
}
